import { useMemo, useRef } from 'react';
import { useInViewport } from 'ahooks';

export type TRef = HTMLDivElement & HTMLSpanElement;

// `root` is omitted: ahooks' `useInViewport` only reads it once when constructing the observer
// (unlike react-use's `useIntersection`, which rebuilt on every `root` change), so exposing it here
// would silently ignore later changes. No current consumer passes it.
export type UseIsVisibleOptions = Omit<IntersectionObserverInit, 'root'>;

export function useIsVisible<T extends HTMLElement = TRef>(options: UseIsVisibleOptions = {}) {
  const ref = useRef<T>(null);
  const { rootMargin, threshold } = options;
  // ahooks' useInViewport reruns its observer-setup effect on [rootMargin, threshold, callback] by
  // reference. rootMargin is a string so value-equal calls are already ===, but threshold can be an
  // array, and an inline literal (e.g. `{ threshold: [0, 0.5] }`) is a fresh reference every render
  // even with identical contents - stringifying it here gives the memo below a stable key.
  const thresholdKey = Array.isArray(threshold) ? threshold.join(',') : threshold;
  const stableOptions = useMemo(
    () => ({ rootMargin, threshold }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- thresholdKey stands in for threshold (a possibly-unstable array)
    [rootMargin, thresholdKey],
  );
  const [isVisible] = useInViewport(ref, stableOptions);

  return useMemo(() => ({ ref, isVisible: isVisible ?? false }) as const, [isVisible]);
}
